import type { Catalogue } from "./catalogue";

/**
 * Romanian.
 *
 * Typed as `Catalogue`, so a key that English has and this does not is a
 * compile error. That is the whole safety property: translations drift the
 * moment they are allowed to be partial.
 *
 * Written as a Romanian speaker would say it, not translated word by word.
 * Football vocabulary follows what clubs here actually use — "lot" for a squad,
 * "legitimat" for a registered player, "etapă" for a matchday.
 */
export const ro: Catalogue = {
  common: {
    save: "Salvează",
    saving: "Se salvează…",
    cancel: "Renunță",
    discard: "Renunță la modificări",
    delete: "Șterge",
    edit: "Editează",
    back: "Înapoi",
    hidden: "Ascuns",
    close: "Închide",
    search: "Caută",
    clear: "Șterge",
    loading: "Se încarcă…",
    signIn: "Autentificare",
    signOut: "Ieși din cont",
    tryAgain: "Încearcă din nou",
    viewAll: "Vezi tot",
    unsaved: "Ai modificări nesalvate.",
    somethingWentWrong: "Ceva n-a mers bine",
    notFoundTitle: "Pagina asta nu există",
    notFoundBody: "Linkul poate fi vechi, sau pagina s-a mutat.",
    noResultsTitle: "Nimic nu se potrivește cu filtrele",
    noResultsBody: "Încearcă o căutare mai largă, sau șterge filtrele ca să vezi tot.",
    clearFilters: "Șterge filtrele",
    previousPage: "Pagina anterioară",
    nextPage: "Pagina următoare",
    of: "din",
  },

  nav: {
    groupClub: "Club",
    groupMatchday: "Zi de meci",
    groupWebsite: "Site",
    groupOrganisation: "Organizație",
    dashboard: "Panou",
    dashboardHint: "Cum stă clubul azi",
    teams: "Echipe",
    teamsHint: "Loturi și grupe de vârstă",
    matches: "Meciuri",
    matchesHint: "Competiții, meciuri și rezultate",
    shop: "Magazin",
    shopHint: "Produse și comenzi",
    players: "Jucători",
    playersHint: "Toți jucătorii legitimați",
    news: "Știri",
    newsHint: "Articole în fiecare limbă în care publici",
    site: "Site și design",
    siteHint: "Șablon, culori și domeniu",
    analytics: "Analiză",
    analyticsHint: "Trafic, surse și conversii",
    marketing: "Email",
    marketingHint: "Newsletter și oferte",
    payments: "Plăți cu cardul",
    paymentsHint: "Banca, și ce i s-a spus",
    tickets: "Bilete",
    ticketsHint: "Stadion, meciuri și porți",
    stadium: "Stadion",
    stadiumHint: "Tribune, sectoare, locuri și porți",
    scanner: "Control acces",
    scannerHint: "Validează bilete la turnichet",
    settings: "Setări",
    settingsHint: "Organizație, limbi și abonament",
    staff: "Staff",
    staffHint: "Antrenori și cine poate intra",
    collapse: "Restrânge",
    expandSidebar: "Extinde meniul",
    collapseSidebar: "Restrânge meniul",
    openClubWebsite: "Deschide site-ul clubului",
    account: "Cont",
    switchClub: "Schimbă clubul",
    current: "curent",
    theme: "Temă de culoare",
    themeLight: "Deschisă",
    themeDark: "Închisă",
    themeSystem: "Ca sistemul",
    commandPlaceholder: "Caută în club…",
    commandEmpty: "Nimic nu se potrivește cu „{query}”.",
    goTo: "Mergi la",
  },

  workspace: {
    chooseTitle: "Alege un club",
    chooseSubtitle: "Autentificat ca {email}.",
    noAccessTitle: "Niciun club",
    noAccessBody:
      "Contul ăsta nu e legat încă de niciun club. Cere-i unui administrator să te invite.",
    unknownSlug: "Nu ai acces la „{slug}”. Alege unul dintre cluburile tale.",
    lastUsed: "ultimul folosit",
  },

  dashboard: {
    viewSite: "Vezi site-ul",
    customise: "Personalizează",
    statPlayers: "Jucători",
    statPlayersHint: "Legitimați la toate echipele",
    statTeams: "Echipe",
    statTeamsHint: "Loturi și grupe de vârstă",
    statDrafts: "Ciorne",
    statDraftsHint: "Articole nepublicate încă",
    statAssistant: "Asistent",
    latestNews: "Ultimele știri",
    latestNewsHint: "Ce e chiar acum pe site-ul clubului.",
    squads: "Loturi",
    squadsHint: "Intră direct în jucătorii unei echipe.",
    getThingsDone: "Treci la treabă",
    getThingsDoneHint: "Cele patru lucruri pe care cluburile le fac cel mai des.",
    writeArticle: "Scrie un articol",
    writeArticleHint: "Cronică de meci, transfer sau anunț",
    registerPlayer: "Legitimează un jucător",
    registerPlayerHint: "Adaugă pe cineva într-un lot",
    changeDesign: "Schimbă designul",
    changeDesignHint: "Șablon, culori și stemă",
    languagesAndPlan: "Limbi și abonament",
    languagesAndPlanHint: "În ce publică clubul",
    nothingPublished: "Nu s-a publicat nimic încă.",
    writeFirst: "Scrie primul articol",
    noTeams: "Nicio echipă încă.",
    playersAcrossTeams: "{players} jucători legitimați la {teams} echipe",
    today: "azi",
    yesterday: "ieri",
    daysAgo: "acum {count} zile",
  },

  players: {
    selectAll: "Selectează toți jucătorii de pe pagină",
    selectedCount: "{count} selectați",
    moveTo: "Mută la…",
    moved: "{count} mutați",
    remove: "Șterge",
    removeSure: "Îi ștergi din club?",
    removeConfirm: "Da, șterge",
    removed: "{count} șterși",
    eyebrow: "Lot",
    title: "Jucători",
    description: "Toți jucătorii legitimați la club, de la toate echipele.",
    register: "Legitimează jucător",
    searchPlaceholder: "Caută după nume…",
    searchLabel: "Caută jucători după nume",
    allTeams: "Toate echipele",
    allStatuses: "Toate statusurile",
    filterTeam: "Filtrează după echipă",
    filterStatus: "Filtrează după status",
    emptyTitle: "Niciun jucător încă",
    emptyBody:
      "Legitimează primul jucător ca să începi cu loturile, antrenamentele și prezența.",
    columnPlayer: "Jucător",
    columnTeam: "Echipă",
    columnPosition: "Post",
    columnStatus: "Status",
    unassigned: "Fără echipă",
    medicalTitle: "Datele medicale sunt separate",
    medicalBody:
      "Staff-ul tehnic vede doar disponibilitatea. Diagnosticele și tratamentele stau sub o permisiune separată și nu apar niciodată pe fișa unui jucător.",
    registration: "Legitimare",
    registrationHint: "Situația la club și la federație.",
    profile: "Profil",
    profileHint: "Cine e jucătorul, pe teren.",
    shirtNumber: "Număr tricou",
    joinedClub: "A venit la club",
    federationId: "ID federație",
    dateOfBirth: "Data nașterii",
    nationality: "Naționalitate",
    preferredFoot: "Piciorul bun",
    positions: "Posturi",
    notFound: "Jucătorul nu există, sau nu e într-o echipă la care ai acces.",
  },

  teams: {
    eyebrow: "Loturi",
    title: "Echipe",
    description: "Toate loturile clubului. Deschide unul ca să vezi jucătorii.",
    emptyTitle: "Nicio echipă",
    emptyBody: "Nu ai acces la nicio echipă din clubul ăsta.",
    senior: "Seniori",
    seniorHint: "Prima echipă și rezervele.",
    academy: "Academie",
    academyHint: "Grupe de vârstă, de la cei mici.",
    playerCount_one: "jucător",
    playerCount_few: "jucători",
    // 20 și peste: "22 de jucători".
    playerCount_other: "de jucători",
  },

  staff: {
    eyebrow: "Oameni",
    title: "Staff",
    description: "Cine conduce echipele și cine poate intra să administreze clubul.",
    technical: "Staff tehnic",
    technicalHint: "Cine conduce o echipă. Antrenorul principal apare pe site-ul clubului.",
    technicalEmpty: "Încă nimeni. Adaugă întâi antrenorul principal — apare pe pagina echipei.",
    team: "Echipa",
    firstName: "Prenume",
    lastName: "Nume",
    role: "Rol",
    add: "Adaugă",
    added: "S-a adăugat.",
    onWebsite: "Pe site",
    accounts: "Conturi",
    accountsHint: "Cine poate intra aici și la ce are voie.",
    accountsEmpty: "Deocamdată doar tu.",
    pending: "Invitat",
    inviteTitle: "Invită pe cineva",
    inviteHint: "Primesc un cont limitat exact la ce alegi aici.",
    email: "Email",
    accessRole: "Ce are voie să facă",
    roleTENANT_OWNER: "Proprietar",
    roleCLUB_ADMIN: "Administrator club",
    roleACADEMY_DIRECTOR: "Director academie",
    roleHEAD_COACH: "Antrenor principal",
    roleCOACH: "Antrenor",
    roleTEAM_MANAGER: "Team manager",
    roleCONTENT_MANAGER: "Presă și comunicare",
    roleFINANCE_MANAGER: "Financiar",
    roleMEDICAL_STAFF: "Kinetoterapeut / medical",
    pickTeamFirst: "alege o echipă",
    chooseTeam: "Alege echipa",
    outOfReach: "peste accesul tău",
    roleOutOfReach: "Nu poți da acces pe care nu îl ai nici tu.",
    startingPassword: "Parolă inițială (opțional)",
    startingPasswordHint:
      "Lasă gol și primește un link ca să și-o aleagă singur — varianta mai bună. Dacă o completezi, e obligat să o schimbe la prima autentificare.",
    startingPasswordPlaceholder: "Minim 10 caractere",
    sendInvite: "Trimite invitația",
    createAccount: "Creează contul",
    invitedByLink: "Invitația a plecat.",
    invitedWithPassword: "Contul e creat. Dă-i parola — o schimbă la prima autentificare.",
    inviteBlocked:
      "Ca să dai acces cuiva, ai nevoie întâi de autentificare în doi pași pe contul tău. Configurează o aplicație de autentificare în setările contului, intră din nou, și apare aici formularul de invitație.",
    inviteRoles: "Roluri pe care le poți da",
    setUpTwoFactor: "Configurează autentificarea în doi pași",
    noAccess: "Fără acces",
    noAccessBody: "Rolul tău nu include administrarea staff-ului clubului.",
  },

  analytics: {
    eyebrow: "Site",
    title: "Analiză",
    description: "Cine intră pe site-ul clubului, de unde vine și ce face.",
    range: "Perioadă",
    rangetoday: "Azi",
    "range7d": "7 zile",
    "range30d": "30 de zile",
    "range90d": "90 de zile",
    live: "Acum pe site",
    sessions: "Vizite",
    visitors: "Vizitatori unici",
    views: "Vizualizări",
    signups: "Înscrieri",
    vsPrevious: "față de perioada anterioară",
    noComparison: "Nu există perioadă anterioară",
    traffic: "Trafic",
    funnel: "De la vizită la comandă",
    funnelHint: "Numărate în vizite, nu în clicuri.",
    stepVisits: "Vizite",
    stepShop: "Au deschis magazinul",
    stepCheckout: "Au început comanda",
    stepOrders: "Au comandat",
    sources: "De unde au venit",
    sourcesEmpty: "Încă n-a venit nimeni de pe alt site.",
    pages: "Cele mai citite",
    devices: "Dispozitiv",
    browsers: "Browser",
    countries: "Țări",
    cities: "Orașe",
    campaigns: "Campanii",
    campaignsEmpty: "Încă nu s-au folosit linkuri de campanie.",
    noData: "Încă nimic.",
    emptyTitle: "Încă nu s-a înregistrat nicio vizită",
    emptyBody:
      "Cifrele apar imediat ce cineva deschide site-ul clubului. Nu trebuie instalat nimic.",
    privacy:
      "Numărate fără cookie-uri. Vizitatorii sunt identificați printr-un hash care se schimbă zilnic, așa că nimeni nu e urmărit de la o zi la alta și nu ai nevoie de banner de consimțământ.",
  },

  marketing: {
    eyebrow: "Suporteri",
    title: "Email",
    description: "Scrie-le celor care au cerut să audă de club.",
    newCampaign: "Trimite ceva",
    newCampaignHint: "Alege scrisoarea, alege cui, vezi câți sunt.",
    campaignName: "Nume",
    campaignNamePlaceholder: "Ofertă — echipament, august",
    template: "Scrisoare",
    chooseTemplate: "Alege o scrisoare",
    audience: "Cine primește",
    audienceNEWSLETTER: "Newsletter",
    audienceSUPPORTERS: "Cei cu cont",
    audienceEVERYONE: "Toți",
    peopleConsented: "persoane au acceptat să primească emailuri de la club",
    sendNow: "Trimite acum",
    send: "Trimite",
    sentTo: "Trimis către {count} persoane.",
    preview: "Previzualizare",
    templates: "Scrisori",
    templatesHint: "Se refolosesc. Oferta de echipament pleacă în fiecare august.",
    newTemplate: "Scrisoare nouă",
    editTemplate: "Editează scrisoarea",
    templateName: "Nume",
    templateSaved: "S-a salvat.",
    subject: "Subiect",
    subjectHint: "Ce se vede primul în inbox. Ține-l sub șaizeci de caractere.",
    preheader: "Rândul de previzualizare",
    preheaderHint: "Rândul de după subiect. Dacă îl lași gol, îl alege clientul de email.",
    body: "Conținut",
    ctaLabel: "Buton",
    ctaHint: "Majoritatea emailurilor unui club au un singur lucru de apăsat.",
    ctaUrl: "Link buton",
    noTemplates: "Nicio scrisoare încă",
    noTemplatesBody: "Scrie una și o poți trimite oricând.",
    history: "Trimise",
    historyHint: "Ce a plecat și către câți.",
    noCampaigns: "Încă nu s-a trimis nimic.",
    untitled: "Fără nume",
    failed: "eșuate",
    statusDRAFT: "Ciornă",
    statusSCHEDULED: "Programat",
    statusSENDING: "Se trimite",
    statusSENT: "Trimis",
    statusFAILED: "Eșuat",
    statusCANCELLED: "Anulat",
    consentNote:
      "Se scrie doar celor care și-au dat acordul, iar fiecare email are dezabonare într-un clic. Cine pleacă dispare imediat de la următoarea trimitere.",
  },

  matches: {
    eyebrow: "Zi de meci",
    title: "Competiții și meciuri",
    description:
      "Competițiile în care ești, meciurile pe care le joci și rezultatele. Tot ce este aici apare pe site-ul clubului.",

    competitions: "Competiții",
    competitionsHint: "O înscriere pentru fiecare competiție și sezon.",
    join: "Înscrie-te într-o competiție",
    joinTitle: "Înscriere în competiție",
    joinBody:
      "Alege competiția și numește sezonul. Dacă un alt club s-a înscris deja în acest sezon, intri în același sezon.",
    competition: "Competiție",
    season: "Sezon",
    seasonPlaceholder: "2025/26",
    startDate: "Începe la",
    endDate: "Se încheie la",
    joined: "Te-ai înscris în {competition}.",
    noCompetitions: "Încă nu ești într-o competiție",
    noCompetitionsBody:
      "Înscrie-te și meciurile, rezultatele și clasamentul apar pe site.",

    fixtures: "Meciuri",
    fixturesHint: "Întâi cele programate, apoi cele jucate.",
    addFixture: "Adaugă meci",
    addFixtureTitle: "Meci nou",
    opponent: "Adversar",
    opponentSearch: "Caută cluburi…",
    opponentMissing: "Nu e în listă? Adaugă „{name}”",
    opponentAdded: "{name} a fost adăugat în directorul de cluburi.",
    venue: "Teren",
    homeAway: "Unde",
    home: "Acasă",
    away: "Deplasare",
    kickoff: "Start",
    kickoffTbc: "Ora urmează să fie confirmată",
    round: "Rundă",
    roundNumber: "Etapa",
    ticketUrl: "Link bilete",
    ticketUrlHint: "Apare ca buton Cumpără bilete lângă acest meci.",
    noFixtures: "Niciun meci încă",
    noFixturesBody: "Adaugă unul și apare pe site la Următoarele meciuri.",
    fixtureAdded: "Meci adăugat.",

    result: "Rezultat",
    editFixture: "Meci",
    edit: "Modifică",
    recordResult: "Trece rezultatul",
    recordResultBody: "Introdu scorul final. Clasamentul se recalculează din el.",
    scoreHome: "Gazde",
    scoreAway: "Oaspeți",
    resultSaved: "Rezultat salvat.",
    saved: "Salvat.",
    status: "Stare",
    statusSCHEDULED: "Programat",
    statusPOSTPONED: "Amânat",
    statusCANCELLED: "Anulat",
    statusFINISHED: "Jucat",
    statusAWARDED: "Câștigat la masa verde",

    table: "Clasament",
    tableHint: "Se calculează din rezultate, nu se editează.",
    position: "#",
    club: "Club",
    played: "M",
    won: "V",
    drawn: "E",
    lost: "Î",
    goalsFor: "GM",
    goalsAgainst: "GP",
    goalDifference: "GD",
    points: "P",
    form: "Formă",
    tableEmpty: "Clasamentul se completează pe măsură ce treci rezultatele.",
    knockoutNoTable: "O competiție cu eliminare directă nu are clasament.",
  },

  squads: {
    staffPhoto: "Fotografie",
    add: "Adaugă echipă",
    addTitle: "Echipă nouă",
    addBody: "Un nume și un cod scurt sunt de ajuns — restul se poate schimba oricând.",
    edit: "Modifică",
    editTitle: "Modifică echipa",
    name: "Nume",
    code: "Cod",
    codeHint: "Apare pe insigne și în tabele. Două-trei litere.",
    ageGroup: "Grupa de vârstă",
    ageGroupPlaceholder: "U15",
    level: "Nivel",
    gender: "Gen",
    isAcademy: "Echipă de academie",
    isAcademyHint: "Echipele de academie sunt grupate separat de echipa de seniori.",
    sport: "Sport",
    sportHint: "Ce joacă echipa asta. Restul clubului rămâne cum e.",
    sportFOOTBALL: "Fotbal",
    sportFUTSAL: "Futsal",
    sportHANDBALL: "Handbal",
    sportBASKETBALL: "Baschet",
    sportVOLLEYBALL: "Volei",
    sportRUGBY: "Rugby",
    sportICE_HOCKEY: "Hochei pe gheață",
    sportWATER_POLO: "Polo pe apă",
    staff: "Staff tehnic",
    staffHint: "Cine conduce echipa. Antrenorul principal apare pe site-ul clubului.",
    staffAdd: "Adaugă",
    staffFirstName: "Prenume",
    staffLastName: "Nume",
    staffRole: "Rol",
    staffTitle: "Funcția",
    staffTitleHint: "Doar dacă la club i se spune altfel.",
    staffPublic: "Arată pe site",
    staffEmpty: "Încă nimeni. Adaugă întâi antrenorul principal — el apare pe pagina echipei.",
    staffRemove: "Scoate",
    roleHEAD_COACH: "Antrenor principal",
    roleASSISTANT_COACH: "Antrenor secund",
    roleGOALKEEPING_COACH: "Antrenor cu portarii",
    roleFITNESS_COACH: "Preparator fizic",
    roleANALYST: "Analist",
    rolePHYSIO: "Kinetoterapeut",
    roleDOCTOR: "Medicul clubului",
    roleTEAM_MANAGER: "Team manager",
    roleKIT_MANAGER: "Magazioner",
    rolePRESS_OFFICER: "Purtător de cuvânt",
    rolePRESIDENT: "Președinte",
    roleDIRECTOR: "Director",
    archive: "Arhivează",
    archiveTitle: "Arhivezi echipa?",
    archiveBody:
      "Dispare din listă, dar își păstrează legitimările și rezultatele. O poți readuce oricând.",
    restore: "Readu",
    created: "Echipă adăugată.",
    updated: "Echipă actualizată.",
    archived: "Echipă arhivată.",

    levelFIRST: "Prima echipă",
    levelRESERVE: "Rezerve",
    levelYOUTH: "Juniori",
    levelFUTSAL: "Futsal",
    levelOTHER: "Altele",
    genderMALE: "Masculin",
    genderFEMALE: "Feminin",
    genderMIXED: "Mixt",
  },

  playerEdit: {
    edit: "Modifică jucătorul",
    details: "Date",
    detailsHint: "Nume, data nașterii și post.",
    photo: "Fotografie",
    photoHint: "Portret, apare pe pagina echipei.",
    firstName: "Prenume",
    lastName: "Nume",
    birthDate: "Data nașterii",
    position: "Post",
    foot: "Piciorul preferat",
    federationId: "Număr legitimație",
    joinedOn: "A venit la club",
    saved: "Jucător actualizat.",

    squad: "Echipă",
    squadHint: "Mutarea unui jucător încheie legitimarea veche și deschide una nouă.",
    team: "Echipa",
    noTeam: "Fără echipă",
    shirtNumber: "Număr tricou",
    moved: "Echipă actualizată.",
    status: "Stare",
    statusTRIAL: "În probe",
    statusREGISTERED: "Legitimat",
    statusLOANED_OUT: "Împrumutat",
    statusINACTIVE: "Inactiv",
    statusDEPARTED: "Plecat",

    footLEFT: "Stângul",
    footRIGHT: "Dreptul",
    footBOTH: "Ambele",
  },

  shop: {
    eyebrow: "Magazin",
    title: "Magazinul clubului",
    description:
      "Ce vinde clubul și cine așteaptă să ridice. Plata se face la casierie.",

    products: "Produse",
    productsHint: "Produsele ascunse rămân în catalog, dar dispar din magazin.",
    add: "Adaugă produs",
    addTitle: "Produs nou",
    editTitle: "Modifică produsul",
    name: "Nume",
    productDescription: "Descriere",
    descriptionHint: "O frază sau două. Apare pe pagina magazinului.",
    price: "Preț",
    image: "Imagine",
    imageHint: "Apare în magazin și în coș.",
    visible: "În magazin",
    visibleHint: "Oprește-l ca să dispară din magazin, fără să îl ștergi.",
    sizes: "Mărimi și stoc",
    sizesHint: "Un rând pentru fiecare mărime. Fără mărimi, produsul primește „Mărime unică”.",
    sizeLabel: "Mărime",
    sku: "Cod",
    stock: "În stoc",
    addSize: "Adaugă mărime",
    created: "Produs adăugat.",
    updated: "Produs actualizat.",
    deleted: "Produs șters.",
    remove: "Șterge",
    removeConfirm: "Ștergi produsul?",
    noProducts: "Încă nimic în magazin",
    noProductsBody: "Adaugă un fular, un tricou sau un program și apare pe site-ul clubului.",
    soldOut: "Stoc epuizat",
    lowStock: "{count} rămase",

    orders: "Comenzi",
    ordersHint: "De ridicat, cele mai noi primele.",
    reference: "Cod",
    buyer: "Cumpărător",
    placed: "Plasată",
    total: "Total",
    collect: "Ridicată",
    cancel: "Anulează",
    collected: "Marcată ca ridicată.",
    cancelled: "Comandă anulată, stocul a revenit.",
    noOrders: "Nicio comandă încă",
    noOrdersBody: "Comenzile de pe site-ul clubului apar aici.",
    statusAWAITING_COLLECTION: "De ridicat",
    statusCOLLECTED: "Ridicată",
    statusCANCELLED: "Anulată",
    statusPENDING: "În așteptare",
  },

  news: {
    cover: "Imagine principală",
    coverHint: "Apare în caruselul de știri de pe prima pagină și pe articol.",
    eyebrow: "Redacție",
    title: "Știri",
    description: "Articolele de pe site-ul clubului, în fiecare limbă în care publici.",
    newArticle: "Articol nou",
    emptyTitle: "Niciun articol încă",
    emptyBody: "Cronicile de meci, transferurile și anunțurile stau toate aici.",
    writeFirst: "Scrie-l pe primul",
    columnTitle: "Titlu",
    columnType: "Tip",
    columnStatus: "Status",
    columnLanguages: "Limbi",
    columnPublished: "Publicat",
    allTypes: "Toate tipurile",
    filterType: "Filtrează după tipul articolului",
    languageReady: "{locale} e gata de publicare",
    languageIncomplete: "{locale} mai are nevoie de text",
    untitled: "Fără titlu",
    whatKind: "Ce fel de articol?",
    articleTitle: "Titlu",
    titlePlaceholder: "Ce s-a întâmplat, într-un rând",
    summary: "Rezumat",
    summaryHint: "Apare în lista de știri și în previzualizarea linkului.",
    summaryPlaceholder: "Una sau două propoziții",
    publishing: "Publicare",
    publishNow: "Publică acum",
    schedule: "Programează",
    sendForReview: "Trimite la verificare",
    backToDraft: "Înapoi la ciornă",
    archive: "Arhivează",
    publishAt: "Publică la",
    saveFirst:
      "Salvează întâi — publicarea trimite ce e stocat, nu ce e pe ecran.",
    missingLocales:
      "Încă nescris în {locales}. Poți publica oricum — cititorii din limbile alea vor vedea versiunea care e gata.",
    discardChanges: "Renunți la modificările nesalvate?",
    created: "Articol creat",
    createdHint: "E ciornă până îl publici.",
    savedIn: "Salvat în {locale}",
  },

  blocks: {
    paragraph: "Paragraf",
    heading: "Subtitlu",
    quote: "Citat",
    list: "Listă",
    bulleted: "Cu buline",
    numbered: "Numerotată",
    addItem: "Adaugă rând",
    moveUp: "Mută mai sus",
    moveDown: "Mută mai jos",
    removeBlock: "Șterge blocul",
    sectionTitle: "Titlu de secțiune",
    quotePlaceholder: "Ce s-a spus, exact",
    attributionPlaceholder: "Cine a spus",
    paragraphPlaceholder: "Scrie paragraful",
    nothingYet: "Nimic aici încă.",
  },

  assistant: {
    title: "Asistent de scriere",
    tagline: "Îți strânge textul. Nu inventează niciodată fapte.",
    improve: "Îmbunătățește textul",
    improving: "Se lucrează…",
    headlines: "Propune titluri",
    thinking: "Se gândește…",
    keepMine: "Păstrez ce am scris",
    useSuggestion: "Folosește sugestia",
    yours: "Al tău",
    suggested: "Sugerat",
    headlinesTitle: "Titluri",
    left: "rămase",
    emptyDraft:
      "Scrie un titlu și un prim paragraf, apoi asistentul are pe ce lucra.",
    unavailable: "Asistentul nu e disponibil acum.",
    notInPlan: "Asistentul de scriere nu e inclus în abonamentul tău.",
  },

  ticketing: {
    eyebrow: "Zi de meci",
    stadiumTitle: "Stadion",
    stadiumDescription:
      "Terenul așa cum îl desenează clubul. O configurație publicată este înghețată: meciurile își iau propria copie, așa că redesenarea unei tribune nu mută pe nimeni care și-a cumpărat deja locul.",
    matchesTitle: "Meciuri",
    matchesDescription:
      "Meciuri cu bilete în vânzare. Fiecare are propriul stadion înghețat și propriul inventar.",
    scannerTitle: "Control acces",
    scannerDescription:
      "Validează un bilet la poartă. Aceleași rute pe care le va folosi și scannerul mobil.",
    configuration: "Configurație",
    version: "Versiunea",
    draft: "Ciornă",
    published: "Publicată",
    archived: "Arhivată",
    publish: "Publică",
    publishHint: "Îngheață această configurație. Editarea ulterioară creează o versiune nouă.",
    newVersion: "Editează ca versiune nouă",
    capacity: "Capacitate",
    reservedSeats: "Locuri numerotate",
    generalAdmission: "Acces general",
    blockedSeats: "Blocate",
    accessibleSeats: "Accesibile",
    byStand: "Pe tribune",
    bySector: "Pe sectoare",
    warnings: "Avertismente",
    noWarnings: "Nimic de semnalat.",
    cannotPublish: "Această configurație nu poate fi publicată încă.",
    publishedToast: "Configurația stadionului a fost publicată.",
    forkedToast: "S-a creat o versiune nouă, ciornă.",
    sector: "Sector",
    sectors: "Sectoare",
    seats: "locuri",
    gate: "Poarta",
    gates: "Porți",
    priceZones: "Zone de preț",
    noStadium: "Niciun stadion încă",
    noStadiumHint: "Creează o arenă și desenează-i tribunele ca să poți vinde bilete.",
    noMatches: "Niciun meci în vânzare",
    noMatchesHint: "Creează un meci pornind de la o configurație publicată.",
    kickoff: "Start",
    doorsOpen: "Porți deschise",
    sold: "Vândute",
    available: "Disponibile",
    held: "În coșuri",
    allocated: "Alocate",
    occupancy: "Grad de ocupare",
    revenue: "Încasări",
    grossRevenue: "Brut",
    netRevenue: "Net, fără TVA",
    ticketsIssued: "Bilete emise",
    seasonTickets: "Abonamente",
    admitted: "Intrări",
    noShows: "Neprezentări",
    refused: "Respinse",
    byTicketType: "Pe tip de bilet",
    byGate: "Pe porți",
    selectEvent: "Alege meciul",
    selectGate: "Alege poarta",
    startScanning: "Pornește scanarea",
    stopScanning: "Oprește",
    manualEntry: "Sau scrie codul biletului",
    scan: "Validează",
    scanAgain: "Următorul bilet",
    recentScans: "Scanări recente",
    noScans: "Nimic scanat încă.",
    cameraUnavailable: "Nu există cameră pe acest dispozitiv. Scrie codul manual.",
    resultValid: "Poftiți înăuntru",
    resultAlreadyUsed: "Deja scanat",
    resultWrongGate: "Poartă greșită",
    resultWrongEvent: "Alt meci",
    resultNotYetValid: "Prea devreme",
    resultExpired: "Expirat",
    resultCancelled: "Anulat",
    resultRefunded: "Returnat",
    resultDeviceRevoked: "Scanner revocat",
    resultUnknown: "Bilet invalid",
    firstSeen: "Prima intrare",
    addVenue: "Adaugă un stadion",
    addVenueHint: "Arena în sine: unde este, cât de mare, cum e orientat terenul.",
    venueName: "Numele stadionului",
    venueCode: "Cod intern",
    venueCodeHint: "Prescurtarea folosită pe biletele tipărite și în exporturi.",
    city: "Oraș",
    address: "Adresă",
    expectedCapacity: "Capacitate estimată",
    pitchOrientation: "Orientarea terenului",
    addConfiguration: "Începe o configurație",
    addConfigurationHint:
      "O configurație are nume și versiune — fotbal la capacitate maximă, una redusă, un concert. Meciurile se creează dintr-o configurație publicată.",
    configurationName: "Numele configurației",
    noPriceZones: "Nicio zonă de preț încă.",
    addPriceZone: "Adaugă o zonă de preț",
    zoneName: "Numele zonei",
    zoneColour: "Culoare",
    priceZone: "Zonă de preț",
    stands: "Tribune",
    noStands: "Nicio tribună încă.",
    addStand: "Adaugă o tribună",
    addStandHint: "Alege latura terenului; forma rezultă din ea.",
    standName: "Numele tribunei",
    side: "Latura",
    sideHint: "Unde se află față de teren.",
    sideWest: "Vest (tribuna principală)",
    sideEast: "Est",
    sideNorth: "Nord (peluză)",
    sideSouth: "Sud (peluză)",
    addSector: "Adaugă un sector",
    sectorName: "Numele sectorului",
    reservedSeating: "Locuri numerotate",
    reservedSeatingHint:
      "Pornit: rânduri și locuri pe care suporterul le alege de pe hartă. Oprit: acces general, cu o capacitate și fără locuri individuale.",
    noGates: "Nicio poartă încă.",
    addGate: "Adaugă o poartă",
    addGateHint: "O intrare, și sectoarele spre care duce.",
    gateName: "Numele porții",
    gateCode: "Cod",
    gateKind: "Tip",
    supporterSide: "Primește",
    sideAny: "Orice suporter",
    sideHome: "Doar gazde",
    sideAway: "Doar oaspeți",
    accessibleEntrance: "Intrare accesibilă",
    sectorsServed: "Sectoarele deservite",
    sectorsServedHint: "Un bilet deschide doar porțile care deservesc sectorul lui.",
    generateSeats: "Generează locuri",
    regenerate: "Înlocuiește toate locurile",
    regenerateWarning:
      "Sectorul are deja rânduri. O nouă generare șterge toate locurile din el și creează altele.",
    rowCount: "Număr de rânduri",
    seatsPerRow: "Locuri pe rând",
    rowStart: "Eticheta primului rând",
    firstSeatNumber: "Primul număr de loc",
    rowLabels: "Etichetele rândurilor",
    numbering: "Numerotarea merge",
    leftToRight: "De la stânga la dreapta",
    rightToLeft: "De la dreapta la stânga",
    wheelchairSeats: "Locuri pentru scaun rulant",
    obstructedSeats: "Vizibilitate obturată",
    seatAddressHint: "Rând și loc, separate prin virgulă — A:1, A:2",
    zoomIn: "Apropie",
    zoomOut: "Depărtează",
    resetView: "Resetează",
    filling: "Se umple",
    almostGone: "Aproape epuizat",
    unavailable: "Indisponibil",
  },
  matchday: {
    live: "În direct",
    addEvent: "Notează ce s-a întâmplat",
    addEventHint:
      "Fluxul ligii aduce golurile în câteva minute, iar cartonașele târziu sau deloc sub primele eșaloane. Ce introduci aici rămâne — fluxul nu îl suprascrie.",
    minute: "Minutul",
    player: "Jucător",
    playerHint: "Opțional",
    forWhom: "Pentru",
    eventGoal: "Gol",
    eventYellow: "Cartonaș galben",
    eventRed: "Cartonaș roșu",
    eventSubstitution: "Schimbare",
    recorded: "Notat.",
    lineupTitle: "Echipa de start",
    lineupWaiting:
      "Foaia de echipă vine din fluxul ligii cu aproximativ o oră înainte de start — și mai târziu la eșaloanele mici.",
    addPlayer: "Adaugă un jucător",
    lineupSaved: "Echipa a fost salvată.",
    tapAPlayer: "Apasă un jucător, apoi o poziție",
    tapASlot: "Acum apasă unde joacă",
    emptySlot: "Poziție liberă",
    everyonePlaced: "Toți sunt pe teren.",
    arrangedByClub: "Aranjată de club",
    roundTitle: "Etapa",
    roundHint:
      "Furnizorii etichetează greșit tururile de cupă — un tur preliminar poate ajunge ca \"Final\". Corectura ta se păstrează separat, deci fluxul nu o desface.",
    roundLabel: "Arată etapa ca",
    roundSaved: "Etapa a fost actualizată.",
  },
  payments: {
    eyebrow: "Bani",
    title: "Plăți cu cardul",
    description:
      "Încasarea cardului unui supporter în magazinul clubului. Banca emite aceste date clubului după semnarea contractului de procesare — până nu sunt aici și pornite, magazinul încasează doar la casă.",
    userName: "Utilizator",
    password: "Parolă",
    passwordHint: "Așa cum ți-a dat-o banca.",
    passwordKeptHint: "Lasă gol ca să păstrezi parola salvată. Nu se mai poate afișa.",
    passwordStored: "Parolă salvată",
    sandbox: "Gateway de test",
    sandboxHint:
      "Cardurile nu se debitează și niciun ban nu se mișcă. Oprește-l doar cu datele emise de bancă pentru plăți reale.",
    acceptCards: "Acceptă carduri",
    acceptCardsHint:
      "Până nu e pornit, magazinul nu oferă deloc plata cu cardul. Lasă-l oprit cât timp testezi.",
    check: "Verifică datele",
    checkPassed: "Banca a răspuns. Datele sunt bune.",
    saved: "Salvat",
    live: "Încasează carduri",
    notLive: "Nu încasează carduri",
    callsTitle: "Tot ce s-a spus băncii",
    callsHint:
      "Fiecare apel, păstrat cum a plecat. Asta îi arăți băncii când nu sunteți de acord asupra unei plăți.",
    noCalls: "Încă nimic.",
  },
  site: {
    title: "Site și design",
    description:
      "Cum arată site-ul public al {club}. Patru șabloane, un singur model de conținut — schimbarea șablonului schimbă cum arată site-ul, niciodată ce conține.",
    template: "Șablon",
    templateHint: "Alege aranjarea. Fiecare e arătat în culorile tale.",
    colours: "Culori",
    coloursHint: "Contrastul e verificat cu aceeași matematică pe care o folosește site-ul.",
    startFrom: "Pornește de la",
    resolvedPalette: "Paleta rezultată — ce va folosi site-ul de fapt",
    identity: "Identitate",
    identityHint: "Ce apare sub numele clubului.",
    footer: "Subsol",
    footerHint: "Contact, adresă și rândul legal din josul fiecărei pagini.",
    footerEmail: "Email",
    footerPhone: "Telefon",
    footerAddress: "Adresă",
    footerAddressHint: "Scrisă cum ai scrie-o pe un plic. Rândurile rămân cum le pui.",
    footerLegal: "Rând legal",
    footerLegalHint: "CIF, număr de înregistrare — ce trebuie să afișeze clubul.",
    footerSponsorsTitle: "Titlu parteneri",
    footerSponsorsHint: "Apare doar după ce adaugi un partener.",
    footerSponsorsPlaceholder: "Parteneri",
    clubName: "Numele clubului",
    clubNameHint: "Așa cum apare în antet și în subsol.",
    wordmark: "Nume scurt",
    wordmarkHint: "Lângă emblemă, în antet.",
    tagline: "Slogan",
    taglineHint: "Un rând scurt. Stă sub stemă, pe prima pagină.",
    colourMode: "Mod de culoare",
    colourModeHint: "Cât din culoarea clubului folosește site-ul public.",
    livePreview: "Site live",
    reloadPreview: "Reîncarcă previzualizarea",
    openInNewTab: "Deschide în tab nou",
    previewDevice: "Dispozitiv",
    desktop: "Desktop",
    mobile: "Telefon",
    unsavedDesign: "Ai modificări de design nesalvate.",
    saveChanges: "Salvează modificările",
    saved: "Design salvat",
    savedHint: "Site-ul clubului se actualizează în câteva secunde.",
    couldNotSave: "N-am putut salva",
    focalPointHint: "Dă clic pe partea care trebuie să rămână în cadru",
    primary: "Principală",
    primaryHint: "Butoane, navigație activă, linkuri și inelul de focus.",
    secondary: "Secundară",
    secondaryHint: "Accente de sprijin. Opțională.",
    accent: "Accent",
    accentHint: "Evidențieri ocazionale. Opțională.",
    contrastOnWhite: "{ratio}:1 pe alb",
    textUses: "textul folosește",
  },

  publicSite: {
    home: "Acasă",
    news: "Știri",
    teams: "Echipe",
    club: "Club",
    nextMatches: "Următoarele meciuri",
    nextMatch: "Meciul următor",
    liveNow: "În direct",
    events: "Fazele meciului",
    goal: "Gol",
    ownGoal: "Autogol",
    penalty: "Penalty",
    missedPenalty: "Penalty ratat",
    yellowCard: "Cartonaș galben",
    redCard: "Cartonaș roșu",
    substitution: "Schimbare",
    calendar: "Calendar",
    matchday: "Etapa",
    kickoffTbc: "Ora TBC",
    clubFeed: "De la club",
    newsletterTitle: "Nu rata nicio etapă",
    newsletterBody: "Meciuri, rezultate, transferuri și vești despre bilete, direct pe email.",
    newsletterEmail: "Adresa ta de email",
    newsletterSubmit: "Abonează-te",
    newsletterDone: "Ești pe listă. Ne vedem la stadion.",
    newsletterFailed: "Nu a mers. Mai încearcă într-un minut.",
    newsletterConsent: "Te poți dezabona din orice email trimis de club.",
    clubFeedHint: "Știri, transferuri, plecări și tot restul.",
    typeANNOUNCEMENT: "Știri",
    typeMATCH_REPORT: "Cronică",
    typeMATCH_PREVIEW: "Avancronică",
    typeSIGNING: "Transfer",
    typeDEPARTURE: "Plecare",
    typeACADEMY: "Academie",
    typeINTERVIEW: "Interviu",
    days: "Zile",
    hours: "Ore",
    minutes: "Min",
    secondsShort: "Sec",
    fullCalendar: "Vezi calendarul",
    previous: "Înapoi",
    next: "Înainte",
    results: "Rezultate",
    standings: "Clasament",
    buyTickets: "Cumpără bilete",
    played: "M",
    goalDifference: "GD",
    differenceGOAL: "GM",
    differencePOINT: "DP",
    differenceSET: "DS",
    unitGOAL: "Goluri",
    unitPOINT: "Puncte",
    unitSET: "Seturi",
    points: "P",
    form: "Formă",
    latestNews: "Ultimele știri",
    allNews: "Toate știrile",
    ourTeams: "Echipele noastre",
    senior: "Seniori",
    seniorFootball: "Fotbal seniori",
    academy: "Academie",
    theClub: "Clubul",
    history: "Palmares și istoric",
    historyLead: "Sezon după sezon, și unde ne-a lăsat.",
    seasonCol: "Sezon",
    competitionCol: "Competiție",
    positionCol: "Loc",
    playedCol: "M",
    recordCol: "V–E–Î",
    honours: "Trofee",
    ground: "Stadion",
    capacity: "Capacitate",
    founded: "Înființat",
    established: "Fondat",
    country: "Țara",
    timeZone: "Fus orar",
    squad: "Lot",
    playerCount_one: "{count} jucător",
    playerCount_few: "{count} jucători",
    playerCount_other: "{count} de jucători",
    teamsLead: "Toate echipele clubului, de la prima echipă până la cea mai mică grupă de academie.",
    viewSquad: "Vezi lotul",
    firstTeam: "Prima echipă",
    roleHEAD_COACH: "Antrenor principal",
    roleASSISTANT_COACH: "Antrenor secund",
    roleGOALKEEPING_COACH: "Antrenor cu portarii",
    roleFITNESS_COACH: "Preparator fizic",
    roleANALYST: "Analist",
    rolePHYSIO: "Kinetoterapeut",
    roleDOCTOR: "Medicul clubului",
    roleTEAM_MANAGER: "Team manager",
    roleKIT_MANAGER: "Magazioner",
    staffTitle: "Staff tehnic",
    teamCount_one: "{count} echipă",
    rolePRESS_OFFICER: "Purtător de cuvânt",
    rolePRESIDENT: "Președinte",
    roleDIRECTOR: "Director",
    teamCount_few: "{count} echipe",
    teamCount_other: "{count} de echipe",
    academyGroups: "Grupe de academie",
    registeredPlayers: "Jucători legitimați",
    squadUnpublished: "Lotul acestei echipe nu a fost publicat încă.",
    squadComingSoon: "Lotul urmează să fie anunțat",
    andMorePlayers: "+{count}",
    noNews: "Nicio știre încă.",
    staffSignIn: "Autentificare staff",
    footerContact: "Contact",
    footerAddress: "Adresă",
    footerFollow: "Urmărește-ne",
    footerSponsors: "Parteneri",
    signIn: "Intră în cont",
    createAccount: "Creează cont",
    signOut: "Ieși din cont",
    myAccount: "Contul meu",
    accountNav: "Cont",
    menuOpen: "Deschide meniul",
    menuClose: "Închide meniul",
    accountLead: "Datele tale la acest club și tot ce ai cumpărat de aici.",
    accountAnonTitle: "Intră în contul de suporter",
    accountAnonBody:
      "Un singur cont pentru tot ce cumperi de la club. Poți comanda și fără cont, ca invitat.",
    myDetails: "Datele tale",
    myDetailsHint: "Le vede doar clubul ăsta.",
    myOrders: "Comenzile tale",
    myOrdersHint: "Tot ce ai cumpărat din magazinul clubului.",
    noOrders: "Încă nimic. Ce cumperi din magazinul clubului apare aici.",
    detailsSaved: "S-a salvat.",
    detailsFailed: "Nu s-a salvat. Mai încearcă o dată.",
    marketingOptIn: "Vreau pe email știri și oferte de la club",
    marketingHint: "Poți opri oricând, și fiecare email are link de dezabonare.",
    closeAccount: "Închide contul",
    closeAccountHint:
      "Îți șterge datele de la acest club. Comenzile deja plasate rămân la club, ca acte ale lui, dar nu mai sunt legate de tine.",
    closeAccountConfirm: "Închizi contul la acest club?",
    orderPlacedOn: "Plasată {date}",
    orderCollectedOn: "Ridicată {date}",
    statusPENDING: "În așteptare",
    statusAWAITING_COLLECTION: "Gata de ridicare",
    statusCOLLECTED: "Ridicată",
    statusCANCELLED: "Anulată",
    shop: "Magazin",
    shopOfficial: "Magazin oficial",
    shopLead: "Produse oficiale ale clubului. Plătești la magazin când ridici.",
    shopEmpty: "Magazinul nu este încă deschis.",
    addToBasket: "Adaugă în coș",
    soldOut: "Stoc epuizat",
    lowStock: "{count} rămase",
    basket: "Coș",
    basketEmpty: "Coșul tău este gol.",
    total: "Total",
    checkout: "Finalizează",
    yourName: "Numele tău",
    email: "Email",
    phone: "Telefon",
    orderNote: "Vrei să ne spui ceva?",
    placeOrder: "Trimite comanda",
    payOnCollection: "Plătești la magazinul clubului, când ridici.",
    payByCard: "Plătește cu cardul",
    payAtCounter: "Plătesc când ridic",
    cardSafe: "Vei fi dus la banca ta ca să plătești.",
    orderPlaced: "Comandă plasată",
    orderReference: "Spune acest cod la magazinul clubului când ridici.",
    orderDone: "Gata",
    remove: "Scoate",
    size: "Mărime",
    keepShopping: "Continuă cumpărăturile",
    ticketsEyebrow: "Bilete",
    ticketsTitle: "Cumpără bilete",
    ticketsNone: "Niciun meci în vânzare acum.",
    ticketsSeatsLeft: "libere",
    ticketsSoldOut: "Epuizat",
    ticketsChooseSector: "Alege unde vrei să stai",
    ticketsChooseSeats: "Alege-ți locurile",
    ticketsBack: "Toate sectoarele",
    ticketsAvailable: "Liber",
    ticketsUnavailable: "Ocupat",
    ticketsSelected: "Ale tale",
    ticketsFrom: "de la",
    ticketsYourSeats: "Locurile tale",
    ticketsNoneChosen: "Alege un sector, apoi apasă pe locurile dorite.",
    ticketsBestAvailable: "Alege tu pentru mine",
    ticketsHowMany: "Câte",
    ticketsHoldExpires: "Rezervate pentru tine:",
    ticketsHoldExpired: "Rezervarea a expirat. Te rugăm să alegi din nou.",
    ticketsBuy: "Cumpără biletele",
    ticketsShow: "Vezi biletele mele",
    ticketsGeneralAdmission: "În picioare — sectorul nu are locuri numerotate.",
    ticketsStanding: "în picioare",
    ticketsRowSeat: "Rând/loc",
    ticketsGate: "Intri pe",
    ticketsKeepSafe: "Arată codul la turnichet. O captură de ecran e suficientă.",
    lineupTitle: "Echipele de start",
    lineupSubstitutes: "Rezerve",
    lineupCoach: "Antrenor",
    language: "Limba",
    readMore: "Citește mai mult",
    translationMissing:
      "Articolul nu e tradus încă — citești altă limbă.",
  },

  settings: {
    eyebrow: "Organizație",
    title: "Setări",
    description: "Ce e organizația asta și ce include abonamentul ei.",
    demoData: "date demo",
    tenant: "Organizație",
    tenantHint: "Stabilite când a fost înrolat clubul.",
    legalName: "Denumire legală",
    tradingName: "Denumire comercială",
    slug: "Identificator",
    status: "Status",
    currency: "Moneda implicită",
    timezone: "Fus orar",
    languages: "Limbi",
    languagesHint: "Fiecare articol poate exista în fiecare dintre ele. Prima e implicită.",
    default: "implicită",
    interfaceLanguage: "Limba interfeței",
    interfaceLanguageHint:
      "În ce limbă e afișat adminul. Se stabilește pentru tot clubul, iar tu ți-o poți schimba doar pentru tine.",
    assistant: "Asistent de scriere",
    assistantHint: "Inclus în abonament și plătit de platformă.",
    available: "Disponibil",
    notAvailable: "Indisponibil",
    assistantBody:
      "Îmbunătățește exprimarea unui articol pe care l-ai scris deja. Nu adaugă niciodată fapte.",
    thisMonth: "Luna asta",
    yourAccess: "Accesul tău",
    yourAccessHint: "Ce poate face {email} în {club}.",
  },
};
